<template>
  <div class="contrats-container">
    <div class="contrats-header">
      <img src="@/assets/icons/cle.png" alt="clé" class="contrats-icon" />
      <span class="contrats-title">Liste des contrats</span>
    </div>
    <div class="contrats-content">
      <!-- Filtres -->
      <div class="contrats-filtres">
        <input 
          v-model="searchNom"
          type="text" 
          placeholder="Chercher par nom" 
          class="contrats-filtre-input"
          @input="handleFilterChange"
        />
        <input 
          v-model="searchBien"
          type="text" 
          placeholder="Par Bien" 
          class="contrats-filtre-input"
          @input="handleFilterChange"
        />
        <select 
          v-model="filterStatut"
          class="contrats-filtre-select"
          @change="handleFilterChange"
        >
          <option value="">Statut</option>
          <option value="active">Actif</option>
          <option value="pending">En attente</option>
          <option value="expired">Expiré</option>
          <option value="terminated">Terminé</option>
        </select>
        <select 
          v-model="filterType"
          class="contrats-filtre-select"
          @change="handleFilterChange"
        >
          <option value="">Type de bien</option>
          <option value="apartment">Appartement</option>
          <option value="shop">Magasin</option>
          <option value="office">Bureau</option>
          <option value="house">Maison</option>
          <option value="land">Terrain</option>
        </select>
        <select 
          v-model="filterEcheance"
          class="contrats-filtre-select"
          @change="handleFilterChange"
        >
          <option value="">Échéance</option>
          <option value="monthly">Mensuel</option>
          <option value="quarterly">Trimestriel</option>
          <option value="semestriel">Semestriel</option>
          <option value="annuel">Annuel</option>
        </select>
      </div>

      <!-- Message d'erreur -->
      <div v-if="error" class="error-message">
        {{ error }}
      </div>

      <!-- État de chargement -->
      <div v-if="isLoading" class="loading-container">
        <div class="loading-spinner"></div>
        <div class="loading-text">Chargement des contrats...</div>
      </div>

      <!-- Table des contrats -->
      <table v-else class="contrats-table">
        <thead>
          <tr>
            <th>Réf</th>
            <th>Client</th>
            <th>PROJET</th>
            <th>Type</th>
            <th>Durée</th>
            <th>Date début</th>
            <th>Prix</th>
            <th>Échéances</th>
            <th>Statut</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="contrats.length === 0">
            <td colspan="10" class="empty-message">
              Aucun contrat trouvé
            </td>
          </tr>
          <tr v-else v-for="(contrat, index) in contrats.filter(c => c)" :key="index">
            <td>{{ contrat?.reference || 'N/A' }}</td>
            <td>{{ contrat?.tenant?.name || 'N/A' }}</td>
            <td>{{ contrat?.property?.address || 'N/A' }}</td>
            <td>{{ contrat?.property?.type || 'N/A' }}</td>
            <td>{{ contrat?.duration || 'N/A' }} mois</td>
            <td>{{ contrat?.start_date || 'N/A' }}</td>
            <td>{{ contrat?.rent_amount || 'N/A' }}</td>
            <td>{{ contrat?.payment_frequency || 'N/A' }}</td>
            <td>
              <span :class="(contrat?.status === 'active') ? 'statut-actif' : 'statut-ech'">{{ contrat?.status || 'En attente' }}</span>
            </td>
            <td>
              <span class="action" @click="openPdfModal(contrat)">
                <img src="@/assets/icons/voir.png" alt="Voir" class="action-icon" />
              </span>
              <span class="action" @click="openFacturesModal(contrat)">
                <img src="@/assets/icons/listf.png" alt="Factures" class="action-icon" />
              </span>
              <span class="action" @click="openEditModal(contrat)">
                <img src="@/assets/icons/modif.png" alt="Modifier" class="action-icon" />
              </span>
              <span class="action" @click="openDeleteModal(contrat)">
                <img src="@/assets/icons/temoin.png" alt="Supprimer" class="action-icon delete-icon" />
              </span>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Pagination -->
      <div v-if="totalItems > perPage" class="contrats-pagination">
        <button 
          class="contrats-page-btn" 
          :disabled="currentPage === 1 || isLoading"
          @click="changePage(currentPage - 1)"
        >&lt;</button>
        
        <button 
          v-for="page in lastPage" 
          :key="page"
          class="contrats-page-btn"
          :class="{ active: page === currentPage }"
          :disabled="isLoading"
          @click="changePage(page)"
        >
          {{ page }}
        </button>
        
        <button 
          class="contrats-page-btn" 
          :disabled="currentPage === lastPage || isLoading"
          @click="changePage(currentPage + 1)"
        >&gt;</button>
      </div>
    </div>
    <div class="contrats-btn-row">
      <button class="contrats-btn" @click="showCreateModal = true">Nouveau Contrat</button>
    </div>

    <ContratWizard 
      :show="showCreateModal"
      @close="showCreateModal = false"
      @contract-created="handleContratCreated"
    />

    <!-- Modal PDF Contrat (Enhanced Design) -->
    <div v-if="showPdfModal" class="modal-overlay" @click.self="closeModals">
      <div class="contrat-modal-enhanced">
        <div class="contrat-modal-header-enhanced">
          <div class="header-content">
            <div class="header-icon">📋</div>
            <div class="header-text">
              <h2>Contrat <span class="contract-ref">{{ selectedContrat?.reference }}</span></h2>
              <p class="header-subtitle">Visualisation du document contractuel</p>
            </div>
          </div>
          <button class="contrat-modal-close-enhanced" @click="closeModals">&times;</button>
        </div>
        
        <div class="contrat-modal-info-enhanced">
          <div class="info-card-compact">
            <div class="info-header">
              <div class="info-icon">👤</div>
              <h3>Informations Locataire</h3>
            </div>
            <div class="info-grid-compact">
              <div v-if="selectedContrat?.tenant" class="info-details">
                <div class="info-row">
                  <span class="label">Nom:</span>
                  <span class="value">{{ selectedContrat.tenant?.name }}</span>
                </div>
                <div class="info-row">
                  <span class="label">Téléphone:</span>
                  <span class="value">{{ selectedContrat.tenant?.phone }}</span>
                </div>
                <div class="info-row">
                  <span class="label">Email:</span>
                  <span class="value">{{ selectedContrat.tenant?.email }}</span>
                </div>
              </div>
              <div v-else class="no-tenant-info">
                <span class="empty-state">Aucune information de locataire disponible</span>
              </div>
            </div>
          </div>
        </div>
        
        <div class="contrat-modal-content-enhanced">
          <div class="pdf-viewer-container">
            <iframe v-if="selectedContrat?.contract_file_path" 
                    :src="`http://localhost:8000/storage/${selectedContrat.contract_file_path}`" 
                    class="pdf-iframe">
            </iframe>
            <div v-else class="no-pdf-enhanced">
              <div class="no-pdf-content">
                <div class="no-pdf-icon">📄</div>
                <h3>Aucun contrat PDF disponible</h3>
                <p>Le document contractuel n'a pas encore été uploadé pour ce contrat.</p>
                <div class="upload-suggestion">
                  <button class="upload-btn-suggestion">
                    <span class="upload-icon">📤</span>
                    Uploader le contrat
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="contrat-modal-footer-enhanced">
          <div class="footer-info">
            <span class="contract-status">
              <span class="status-dot" :class="selectedContrat?.status === 'active' ? 'status-active' : 'status-pending'"></span>
              {{ selectedContrat?.status === 'active' ? 'Contrat Actif' : 'En Attente' }}
            </span>
          </div>
          <div class="footer-actions">
            <button class="action-btn secondary" @click="closeModals">
              <span class="btn-icon">✕</span>
              Fermer
            </button>
            <button class="action-btn primary" 
                    :disabled="!selectedContrat?.contract_file_path"
                    @click="downloadContract(selectedContrat)">
              <span class="btn-icon">📥</span>
              Télécharger
            </button>
            <button class="action-btn primary" 
                    :disabled="!selectedContrat?.contract_file_path"
                    @click="printContract(selectedContrat)">
              <span class="btn-icon">🖨️</span>
              Imprimer
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- Modal Factures (Enhanced Design) -->
    <div v-if="showFacturesModal" class="facture-modal-overlay-enhanced" @click.self="closeModals">
      <div class="facture-modal-enhanced">
        <div class="facture-modal-header-enhanced">
          <div class="header-content">
            <div class="header-icon">📋</div>
            <div class="header-text">
              <h2>Factures - Contrat <span class="contract-ref">{{ selectedContrat?.reference }}</span></h2>
              <p class="header-subtitle">Gestion et visualisation des factures</p>
            </div>
          </div>
          <button class="facture-modal-close-enhanced" @click="closeModals">&times;</button>
        </div>
        
        <div class="facture-modal-content-enhanced">
          <div class="facture-navigation-header">
            <div class="facture-nav-controls">
              <button class="nav-btn-compact" @click="prevFacture" :disabled="currentFactureIndex === 0">
                <span class="nav-icon">←</span>
              </button>
              
              <div class="facture-info-compact">
                <span class="facture-status-compact" :class="factures.length > 0 ? (currentFacture.statut === 'payee' ? 'status-paid' : 'status-unpaid') : 'status-none'">
                  <span v-if="factures.length > 0">{{ currentFacture.statut === 'payee' ? '✓ Payée' : '⏳ Non payée' }}</span>
                  <span v-else>📄 Aucune facture</span>
                </span>
                <span class="facture-counter-compact">
                  <span v-if="factures.length > 0">{{ currentFactureIndex + 1 }} / {{ factures.length }}</span>
                  <span v-else>0 / 0</span>
                </span>
              </div>
              
              <button class="nav-btn-compact" @click="nextFacture" :disabled="currentFactureIndex === factures.length - 1">
                <span class="nav-icon">→</span>
              </button>
            </div>
          </div>
          
          <div class="pdf-viewer-container">
            <iframe v-if="currentFacture.apiData?.invoice_file_path" 
                    :src="`http://localhost:8000/storage/${currentFacture.apiData.invoice_file_path}`" 
                    class="pdf-iframe">
            </iframe>
            <div v-else class="no-pdf-enhanced">
              <div class="no-pdf-content">
                <div class="no-pdf-icon">📄</div>
                <h3 v-if="factures.length > 0">Aucune facture PDF disponible</h3>
                <h3 v-else>Aucune facture trouvée</h3>
                <p v-if="factures.length > 0">Le fichier PDF de la facture n'a pas encore été généré.</p>
                <p v-else>Aucune facture n'a été générée pour ce contrat.</p>
                <div v-if="factures.length > 0" class="facture-details-fallback">
                  <div class="detail-row">
                    <span class="detail-label">Référence:</span>
                    <span class="detail-value">{{ currentFacture.numero }}</span>
                  </div>
                  <div class="detail-row">
                    <span class="detail-label">Date:</span>
                    <span class="detail-value">{{ currentFacture.date }}</span>
                  </div>
                  <div class="detail-row">
                    <span class="detail-label">Montant:</span>
                    <span class="detail-value">{{ currentFacture.montant || 'Non spécifié' }} MAD</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="facture-thumbnails-enhanced" v-if="factures.length > 1">
          <div class="thumbnails-container">
            <div v-for="(facture, idx) in factures" :key="facture.id"
                 :class="['thumbnail-enhanced', { active: idx === currentFactureIndex }]"
                 @click="selectFacture(facture, idx)">
              <div class="thumbnail-header">
                <span class="thumbnail-ref">{{ facture.numero }}</span>
                <span class="thumbnail-status" :class="facture.statut === 'payee' ? 'paid' : 'unpaid'"></span>
              </div>
              <div class="thumbnail-date">{{ facture.date }}</div>
            </div>
          </div>
        </div>
        
        <div class="facture-modal-footer-enhanced">
          <div class="footer-info">
            <span class="facture-summary">
              <span v-if="factures.length > 0">{{ factures.filter(f => f.statut === 'payee').length }} payée(s) / {{ factures.length }} total</span>
              <span v-else>Aucune facture</span>
            </span>
          </div>
          <div class="footer-actions">
            <!-- Add the new "Ajouter Facture" button -->
            <button class="action-btn success" @click="openAddFactureModal">
              <span class="btn-icon">➕</span>
              Ajouter Facture
            </button>
            <button class="action-btn secondary" @click="closeModals">
              <span class="btn-icon">✕</span>
              Fermer
            </button>
            <button class="action-btn primary" 
                    :disabled="factures.length === 0 || (!currentFacture.apiData?.invoice_file_path && !currentFacture.isGenerated)"
                    @click="downloadFacture(currentFacture)">
              <span class="btn-icon">📥</span>
              Télécharger
            </button>
            <button class="action-btn primary" 
                    :disabled="factures.length === 0 || (!currentFacture.apiData?.invoice_file_path && !currentFacture.isGenerated)"
                    @click="printFacture(currentFacture)">
              <span class="btn-icon">🖨️</span>
              Imprimer
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- Modal Edition Contrat - Design Moderne -->
    <div v-if="showEditModal" class="edit-modal-overlay" @click.self="closeModals">
      <div class="edit-modal">
        <!-- Header -->
        <div class="edit-modal-header">
          <div class="header-left">
            <h2>Modifier le contrat</h2>
            <p>Référence: <strong>{{ selectedContrat?.reference || 'N/A' }}</strong></p>
          </div>
          <button class="close-btn" @click="closeModals">&times;</button>
        </div>

        <!-- Body -->
        <div class="edit-modal-body">
        <form @submit.prevent="saveEdit">
            
            <!-- Section Info -->
            <div class="info-card readonly">
              <div class="card-header">
                <div class="icon">🔒</div>
                <div>
                  <h3>Informations fixes</h3>
                  <p>Non modifiables après création</p>
                </div>
              </div>
              <div class="info-grid">
                <div class="info-item">
            <label>Client</label>
                  <div class="readonly-value">{{ editContrat.client }}</div>
          </div>
                <div class="info-item">
                  <label>Projet</label>
                  <div class="readonly-value">{{ editContrat.projet }}</div>
          </div>
                <div class="info-item">
            <label>Type</label>
                  <div class="readonly-value">{{ editContrat.type }}</div>
          </div>
              </div>
            </div>

            <!-- Section Contrat -->
            <div class="info-card editable">
              <div class="card-header">
                <div class="icon">📋</div>
                <div>
                  <h3>Détails du contrat</h3>
                  <p>Durée et dates</p>
                </div>
              </div>
              <div class="form-grid">
                <div class="form-field">
                  <label>Durée (calculée automatiquement)</label>
                  <div class="calculated-field">
                    <input v-model="calculatedDuration" type="text" readonly />
                    <span class="calc-icon">📊</span>
                  </div>
                </div>
                <div class="form-field">
                  <label>Statut</label>
                  <select v-model="editContrat.statut" required>
                    <option value="">Choisir</option>
                    <option value="pending">En attente</option>
                    <option value="active">Actif</option>
                    <option value="expired">Expiré</option>
                    <option value="terminated">Terminé</option>
            </select>
          </div>
                <div class="form-field">
            <label>Date début</label>
                  <input v-model="editContrat.debut" type="date" required @change="calculateDuration" />
          </div>
                <div class="form-field">
                  <label>Date fin</label>
                  <input v-model="editContrat.fin" type="date" required @change="calculateDuration" />
          </div>
                <div class="form-field">
                  <label>Première paiement</label>
                  <input v-model="editContrat.premierePaiement" type="date" />
                </div>
                <div class="form-field">
                  <label>Fréquence</label>
            <select v-model="editContrat.echeance" required>
                    <option value="">Choisir</option>
              <option value="Mensuel">Mensuel</option>
              <option value="Trimestriel">Trimestriel</option>
              <option value="Semestriel">Semestriel</option>
              <option value="Annuel">Annuel</option>
            </select>
          </div>
              </div>
            </div>

            <!-- Section Finances -->
            <div class="info-card editable">
              <div class="card-header">
                <div class="icon">💰</div>
                <div>
                  <h3>Tarification</h3>
                  <p>Montants et taxes</p>
                </div>
              </div>
              <div class="form-grid">
                <div class="form-field">
                  <label>Loyer (MAD)</label>
                  <div class="input-with-icon">
                    <input v-model="editContrat.prix" type="number" step="0.01" required />
                    <span class="input-suffix">MAD</span>
                  </div>
                </div>
                <div class="form-field">
                  <label>TVA (%)</label>
                  <div class="input-with-icon">
                    <input v-model="editContrat.tva" type="number" step="0.01" min="0" max="100" />
                    <span class="input-suffix">%</span>
                  </div>
                </div>
                <div class="form-field">
                  <label>Remise (%)</label>
                  <div class="input-with-icon">
                    <input v-model="editContrat.remise" type="number" step="0.01" min="0" max="100" />
                    <span class="input-suffix">%</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Section Notes -->
            <div class="info-card editable">
              <div class="card-header">
                <div class="icon">📝</div>
                <div>
                  <h3>Notes</h3>
                  <p>Commentaires additionnels</p>
                </div>
              </div>
              <div class="form-field">
                <textarea v-model="editContrat.notes" rows="3" placeholder="Ajoutez vos notes ici..."></textarea>
              </div>
            </div>

            <!-- Section Document -->
            <div class="info-card editable">
              <div class="card-header">
                <div class="icon">📄</div>
                <div>
                  <h3>Document du contrat</h3>
                  <p>Fichier PDF ou document attaché</p>
                </div>
              </div>
              <div class="document-section">
                <!-- Current file info -->
                <div v-if="selectedContrat?.contract_file_path || selectedContrat?.document_path" class="current-file">
                  <div class="file-info">
                    <div class="file-icon">📎</div>
                    <div class="file-details">
                      <span class="file-name">Document existant</span>
                      <span class="file-path">{{ getFileName(selectedContrat?.contract_file_path || selectedContrat?.document_path) }}</span>
                    </div>
                  </div>
                </div>
                
                <!-- Upload new file -->
                <div class="form-field">
                  <label>Remplacer ou ajouter un document</label>
                  <div class="file-upload-area" :class="{ 'has-file': newContractFile }">
                    <input 
                      type="file" 
                      ref="fileInput"
                      @change="handleContractFileUpload"
                      accept=".pdf,.doc,.docx"
                      style="display: none;"
                    />
                    <div class="upload-content" @click="triggerFileInput">
                      <div v-if="!newContractFile" class="upload-placeholder">
                        <div class="upload-icon">📁</div>
                        <span>Cliquez pour sélectionner un fichier</span>
                        <small>PDF, DOC, DOCX (max 5MB)</small>
                      </div>
                      <div v-else class="upload-success">
                        <div class="upload-icon">✅</div>
                        <span>{{ newContractFile.name }}</span>
                        <button type="button" class="remove-file-btn" @click.stop="removeNewFile">❌</button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Error Message -->
            <div v-if="updateError" class="error-alert">
              <div class="error-icon">⚠️</div>
              <span>{{ updateError }}</span>
          </div>
        </form>
        </div>

        <!-- Footer -->
        <div class="edit-modal-footer">
          <button type="button" class="btn-cancel" @click="closeModals" :disabled="isUpdating">
            Annuler
          </button>
          <button type="submit" class="btn-save" @click="saveEdit" :disabled="isUpdating">
            <span v-if="isUpdating" class="loading">
              <div class="spinner"></div>
              Enregistrement...
            </span>
            <span v-else>
              💾 Enregistrer
            </span>
          </button>
        </div>
      </div>
    </div>

    <!-- Modal Confirmation Suppression -->
    <DeleteConfirmationModal
      :show="showDeleteModal"
      title="Supprimer le contrat"
      message="Êtes-vous sûr de vouloir supprimer ce contrat ?"
      :entity-details="deleteModalEntityDetails"
      :loading="isDeleting"
      :error="deleteError"
      @cancel="closeModals"
      @confirm="confirmDelete"
    />

    <!-- Add Facture Modal -->
    <div v-if="showAddFactureModal" class="modal-overlay" @click.self="closeAddFactureModal">
      <div class="add-facture-modal">
        <div class="modal-header">
          <h3>Ajouter une nouvelle facture</h3>
          <p class="modal-subtitle">Contrat: {{ selectedContrat?.reference }}</p>
          <button @click="closeAddFactureModal" class="close-btn">&times;</button>
        </div>

        <form @submit.prevent="submitFacture" class="facture-form">
          <div class="form-row">
            <div class="form-group">
              <label>Date d'émission *</label>
              <input type="date" v-model="factureForm.issue_date" required />
            </div>
            <div class="form-group">
              <label>Date d'échéance *</label>
              <input type="date" v-model="factureForm.due_date" required />
            </div>
          </div>

          <div class="form-group">
            <label>Période *</label>
            <input type="text" v-model="factureForm.period" required placeholder="Ex: Janvier 2024" />
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>Montant HT (DH) *</label>
              <input type="number" v-model="factureForm.amount_excl_tax" @input="calculateTotal" required min="0" step="0.01" />
            </div>
            <div class="form-group">
              <label>Montant TVA (DH) *</label>
              <input type="number" v-model="factureForm.tax_amount" @input="calculateTotal" required min="0" step="0.01" />
            </div>
          </div>

          <div class="form-group">
            <label>Montant Total TTC (DH) *</label>
            <input type="number" v-model="factureForm.total_amount" required min="0" step="0.01" readonly />
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>Statut *</label>
              <select v-model="factureForm.status" required>
                <option value="">Sélectionner un statut</option>
                <option value="pending">En attente</option>
                <option value="paid">Payée</option>
                <option value="late">En retard</option>
              </select>
            </div>
            <div class="form-group" v-if="factureForm.status === 'paid'">
              <label>Mode de paiement</label>
              <select v-model="factureForm.payment_method">
                <option value="">Sélectionner un mode</option>
                <option value="cash">Espèces</option>
                <option value="check">Chèque</option>
                <option value="transfer">Virement</option>
                <option value="card">Carte bancaire</option>
              </select>
            </div>
          </div>

          <div class="form-group" v-if="factureForm.status === 'paid'">
            <label>Date de paiement</label>
            <input type="date" v-model="factureForm.payment_date" />
          </div>

          <div class="form-group">
            <label>Fichier facture (PDF) *</label>
            <input type="file" @change="handleInvoiceFileUpload" accept=".pdf,.doc,.docx" required />
          </div>

          <div class="form-group" v-if="factureForm.status === 'paid'">
            <label>Preuve de paiement</label>
            <input type="file" @change="handlePaymentProofUpload" accept=".pdf,.jpg,.jpeg,.png" />
          </div>

          <div class="form-group">
            <label>Notes</label>
            <textarea v-model="factureForm.notes" rows="3" placeholder="Notes supplémentaires..."></textarea>
          </div>

          <div class="form-actions">
            <button type="button" @click="closeAddFactureModal" class="btn-secondary">
              Annuler
            </button>
            <button type="submit" class="btn-primary" :disabled="isSubmittingFacture">
              {{ isSubmittingFacture ? 'Création...' : 'Créer la facture' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import ContratWizard from '@/components/contrats/ContratWizard.vue'
import DeleteConfirmationModal from '@/components/common/DeleteConfirmationModal.vue'
import contratService from '@/services/contratService'

// État des données
const contrats = ref([])
const isLoading = ref(false)
const error = ref(null)

// État des modals
const showPdfModal = ref(false)
const showFacturesModal = ref(false)
const showEditModal = ref(false)
const showCreateModal = ref(false)
const showDeleteModal = ref(false)
const selectedContrat = ref(null)
const editContrat = ref({ 
  client: '', 
  projet: '', 
  type: '', 
  duree: '', 
  debut: '', 
  fin: '', 
  premierePaiement: '',
  prix: '', 
  echeance: '',
  tva: '',
  remise: '',
  statut: '',
  notes: ''
})

// Computed property for calculated duration
const calculatedDuration = computed(() => {
  if (editContrat.value.debut && editContrat.value.fin) {
    const startDate = new Date(editContrat.value.debut)
    const endDate = new Date(editContrat.value.fin)
    
    if (endDate > startDate) {
      const diffTime = Math.abs(endDate - startDate)
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
      const diffMonths = Math.round(diffDays / 30.44) // Average days per month
      
      if (diffMonths < 1) {
        return `${diffDays} jour${diffDays > 1 ? 's' : ''}`
      } else if (diffMonths < 12) {
        return `${diffMonths} mois`
      } else {
        const years = Math.floor(diffMonths / 12)
        const remainingMonths = diffMonths % 12
        if (remainingMonths === 0) {
          return `${years} an${years > 1 ? 's' : ''}`
        } else {
          return `${years} an${years > 1 ? 's' : ''} et ${remainingMonths} mois`
        }
      }
    }
  }
  return editContrat.value.duree || 'Non calculé'
})

// État pour la sauvegarde et suppression
const isUpdating = ref(false)
const isDeleting = ref(false)
const updateError = ref(null)
const deleteError = ref(null)

// Computed property for delete modal entity details
const deleteModalEntityDetails = computed(() => {
  if (!selectedContrat.value) return []
  
  return [
    {
      label: 'Client',
      value: selectedContrat.value?.tenant?.name || 'N/A'
    },
    {
      label: 'Projet',
      value: selectedContrat.value?.property?.address || 'N/A'
    },
    {
      label: 'Montant',
      value: selectedContrat.value?.montant ? `${selectedContrat.value.montant} €` : 'N/A'
    }
  ]
})

// État pour la gestion des fichiers
const newContractFile = ref(null)
const fileInput = ref(null)

// État des filtres
const searchNom = ref('')
const searchBien = ref('')
const filterStatut = ref('')
const filterType = ref('')
const filterEcheance = ref('')

// Pagination
const currentPage = ref(1)
const perPage = ref(10)
const totalItems = ref(0)
const lastPage = ref(1)

// Chargement des contrats avec filtres
async function loadContrats() {
  try {
    isLoading.value = true
    error.value = null

    const filters = {
      tenant_name: searchNom.value,
      property_reference: searchBien.value,
      status: filterStatut.value,
      property_type: filterType.value,
      payment_frequency: filterEcheance.value
    }

    const response = await contratService.getContrats(currentPage.value, perPage.value, filters)
    
    // Extract contracts from paginated response
    if (response.data && response.data.data && Array.isArray(response.data.data)) {
      contrats.value = response.data.data.filter(item => item !== null && item !== undefined)
    } else {
      contrats.value = []
    }
    
    totalItems.value = response.data.total || 0
    currentPage.value = response.data.current_page || 1
    lastPage.value = response.data.last_page || 1
    perPage.value = response.data.per_page || 10

  } catch (err) {
    error.value = "Erreur lors du chargement des contrats. Veuillez réessayer."
    console.error('Erreur lors du chargement des contrats:', err)
  } finally {
    isLoading.value = false
  }
}

// Changement de page
async function changePage(page) {
  if (page >= 1 && page <= lastPage.value && page !== currentPage.value) {
    currentPage.value = page
    await loadContrats()
  }
}

// Gestion des filtres
function handleFilterChange() {
  currentPage.value = 1 // Réinitialiser à la première page lors du filtrage
  loadContrats()
}

// Chargement initial
onMounted(() => {
  loadContrats()
})

// Données pour le slideshow des factures
const factures = ref([])
const currentFactureIndex = ref(0)

// Gestion des modals
function openPdfModal(contrat) {
  if (!contrat) return;
  selectedContrat.value = contrat
  showPdfModal.value = true
}

function openFacturesModal(contrat) {
  if (!contrat) return;
  selectedContrat.value = contrat
  // Générer les factures pour ce contrat
  generateFactures(contrat)
  showFacturesModal.value = true
}

function openEditModal(contrat) {
  if (!contrat) return;
  selectedContrat.value = contrat
  
  // Mapper les valeurs backend vers frontend
  const mapEcheanceToFrontend = (frequency) => {
    const mapping = {
      'monthly': 'Mensuel',
      'quarterly': 'Trimestriel', 
      'semestriel': 'Semestriel',
      'annuel': 'Annuel'
    }
    return mapping[frequency] || 'Mensuel'
  }
  
  editContrat.value = { 
    client: contrat.tenant?.name || '',
    projet: contrat.property?.address || '',
    type: contrat.property?.type || '',
    duree: contrat.duration ? `${contrat.duration} MOIS` : '',
    debut: contrat.start_date || '',
    fin: contrat.end_date || '',
    premierePaiement: contrat.first_payment_date || '',
    prix: contrat.rent_amount || '',
    tva: contrat.tax_rate || '',
    remise: contrat.discount || '',
    echeance: mapEcheanceToFrontend(contrat.payment_frequency),
    statut: contrat.status || '',
    notes: contrat.notes || ''
  }
  showEditModal.value = true
}

function openDeleteModal(contrat) {
  if (!contrat) return;
  selectedContrat.value = contrat
  showDeleteModal.value = true
}

async function confirmDelete() {
  if (!selectedContrat.value) return
  
  try {
    isDeleting.value = true
    deleteError.value = null
    
    console.log('Deleting contract:', selectedContrat.value.id)
    
    const response = await contratService.deleteContrat(selectedContrat.value.id)
    
    if (response.success) {
      // Recharger la liste des contrats
      await loadContrats()
      closeModals()
      
      // Afficher un message de succès
      console.log('Contrat supprimé avec succès')
    } else {
      deleteError.value = 'Erreur lors de la suppression du contrat'
    }
    
  } catch (err) {
    console.error('Erreur lors de la suppression:', err)
    deleteError.value = err.response?.data?.message || 'Erreur lors de la suppression du contrat'
  } finally {
    isDeleting.value = false
  }
}

// File management functions
function handleContractFileUpload(event) {
  const file = event.target.files[0]
  if (file) {
    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      updateError.value = 'Le fichier ne doit pas dépasser 5MB'
      return
    }
    
    // Validate file type
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']
    if (!allowedTypes.includes(file.type)) {
      updateError.value = 'Format de fichier non supporté. Utilisez PDF, DOC ou DOCX'
      return
    }
    
    newContractFile.value = file
    updateError.value = null
  }
}

function triggerFileInput() {
  if (fileInput.value) {
    fileInput.value.click()
  }
}

function removeNewFile() {
  newContractFile.value = null
  if (fileInput.value) {
    fileInput.value.value = ''
  }
}

function getFileName(path) {
  if (!path) return 'Document'
  return path.split('/').pop() || 'Document'
}


function closeModals() {
  showFacturesModal.value = false
  showEditModal.value = false
  showCreateModal.value = false
  showDeleteModal.value = false
  showAddFactureModal.value = false
  selectedContrat.value = null
  editContrat.value = { 
    client: '', 
    projet: '', 
    type: '', 
    duree: '', 
    debut: '', 
    fin: '', 
    premierePaiement: '',
    prix: '', 
    echeance: '',
    tva: '',
    remise: '',
    statut: '',
    notes: ''
  }
}

// Mapper les valeurs d'échéance frontend vers backend
function mapEcheanceToBackend(echeance) {
  const mapping = {
    'Mensuel': 'monthly',
    'Trimestriel': 'quarterly',
    'Semestriel': 'semestriel',
    'Annuel': 'annuel'
  }
  return mapping[echeance] || 'monthly'
}

// Helper function to calculate duration in months for backend
function calculateDurationInMonths(startDate, endDate) {
  if (!startDate || !endDate) return 12 // Default value
  
  const start = new Date(startDate)
  const end = new Date(endDate)
  
  if (end <= start) return 12 // Default value for invalid dates
  
  const diffTime = Math.abs(end - start)
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  const diffMonths = Math.round(diffDays / 30.44) // Average days per month
  
  return diffMonths
}

// Function to calculate duration when dates change
function calculateDuration() {
  if (editContrat.value.debut && editContrat.value.fin) {
    const startDate = new Date(editContrat.value.debut)
    const endDate = new Date(editContrat.value.fin)
    
    if (endDate > startDate) {
      const diffTime = Math.abs(endDate - startDate)
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
      const diffMonths = Math.round(diffDays / 30.44) // Average days per month
      
      // Update the duration value for backend
      editContrat.value.duree = `${diffMonths} MOIS`
    }
  }
}

async function saveEdit() {
  if (!selectedContrat.value) return
  
  try {
    isUpdating.value = true
    updateError.value = null
    
    // Calculate duration before saving
    calculateDuration()
    
    let response
    
    // Check if we have a file to upload
    if (newContractFile.value) {
      // Use FormData for file upload
      const formData = new FormData()
      formData.append('start_date', editContrat.value.debut)
      formData.append('end_date', editContrat.value.fin)
      if (editContrat.value.premierePaiement) {
        formData.append('first_payment_date', editContrat.value.premierePaiement)
      }
      formData.append('duration', calculateDurationInMonths(editContrat.value.debut, editContrat.value.fin))
      formData.append('rent_amount', parseFloat(editContrat.value.prix) || 0)
      formData.append('tax_rate', parseFloat(editContrat.value.tva) || 0)
      formData.append('discount', parseFloat(editContrat.value.remise) || 0)
      formData.append('payment_frequency', mapEcheanceToBackend(editContrat.value.echeance))
      formData.append('status', editContrat.value.statut)
      if (editContrat.value.notes) {
        formData.append('notes', editContrat.value.notes)
      }
      formData.append('contract_file', newContractFile.value)
      
      console.log('Updating contract with file upload')
      console.log('FormData contents:')
      for (let [key, value] of formData.entries()) {
        console.log(key, value)
      }
      response = await contratService.updateContratWithFile(selectedContrat.value.id, formData)
    } else {
      // Regular JSON update without file
      const updateData = {
        start_date: editContrat.value.debut,
        end_date: editContrat.value.fin,
        first_payment_date: editContrat.value.premierePaiement || null,
        duration: calculateDurationInMonths(editContrat.value.debut, editContrat.value.fin),
        rent_amount: parseFloat(editContrat.value.prix) || 0,
        tax_rate: parseFloat(editContrat.value.tva) || 0,
        discount: parseFloat(editContrat.value.remise) || 0,
        payment_frequency: mapEcheanceToBackend(editContrat.value.echeance),
        status: editContrat.value.statut,
        notes: editContrat.value.notes || null
      }
      
      console.log('Updating contract with data:', updateData)
      response = await contratService.updateContrat(selectedContrat.value.id, updateData)
    }
    
    if (response.success) {
      // Recharger la liste des contrats pour refléter les changements
      await loadContrats()
  closeModals()
      
      // Afficher un message de succès (optionnel)
      console.log('Contrat mis à jour avec succès')
    } else {
      updateError.value = 'Erreur lors de la mise à jour du contrat'
    }
    
  } catch (err) {
    console.error('Erreur lors de la mise à jour:', err)
    updateError.value = err.response?.data?.message || 'Erreur lors de la mise à jour du contrat'
  } finally {
    isUpdating.value = false
  }
}

// Fonction pour gérer la création d'un nouveau contrat
function handleContratCreated(newContrat) {
  // Ajouter le nouveau contrat à la liste
  contrats.value.push(newContrat)
  showCreateModal.value = false
}

// Fonctions pour le slideshow des factures
async function generateFactures(contrat) {
  if (!contrat) {
    factures.value = []
    currentFactureIndex.value = 0
    return
  }
  
  try {
    // Récupérer les factures réelles depuis l'API
    const factureService = await import('@/services/factureService').then(m => m.default)
    const response = await factureService.getFacturesContrat(contrat.id)
    
    if (response.data && Array.isArray(response.data)) {
      // Transformer les données de l'API au format attendu et montrer toutes les factures
      factures.value = response.data.map(facture => ({
        id: facture.id,
        numero: facture.reference,
        date: new Date(facture.issue_date).toLocaleDateString('fr-FR'),
        statut: facture.status === 'paid' ? 'payee' : 'non-payee',
        montant: facture.total_amount,
        periode: facture.period,
        apiData: facture, // Garder toutes les données de l'API
        // S'assurer que le chemin du fichier est accessible
        invoice_file_path: facture.invoice_file_path || null
      }))
    } else {
      factures.value = []
    }
    
    // Si aucune facture n'est trouvée, on garde la liste vide
  } catch (error) {
    console.error('Erreur lors de la récupération des factures:', error)
    factures.value = []
  } finally {
    currentFactureIndex.value = 0
  }
}

function prevFacture() {
  if (currentFactureIndex.value > 0) {
    currentFactureIndex.value--
  }
}

function nextFacture() {
  if (currentFactureIndex.value < factures.value.length - 1) {
    currentFactureIndex.value++
  }
}

function selectFacture(facture, idx) {
  if (!facture) return;
  
  if (idx !== undefined) {
    currentFactureIndex.value = idx;
  } else {
    const index = factures.value.findIndex(f => f && f.id === facture.id);
    if (index !== -1) {
      currentFactureIndex.value = index;
    }
  }
}

async function downloadFacture(facture) {
  if (!facture) return;
  
  try {
    if (facture.apiData?.invoice_file_path) {
      // Si la facture vient de l'API et a un fichier PDF
      window.open(`http://localhost:8000/storage/${facture.apiData.invoice_file_path}`, '_blank');
    } else if (facture.isGenerated) {
      // Pour les factures générées, créer un PDF à la volée
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF();
      
      // En-tête de la facture
      doc.setFontSize(20);
      doc.text(`Facture ${facture.numero}`, 105, 20, { align: 'center' });
      
      doc.setFontSize(12);
      doc.text(`Date: ${facture.date}`, 20, 40);
      doc.text(`Période: ${facture.periode}`, 20, 50);
      doc.text(`Montant: ${facture.montant} MAD`, 20, 60);
      doc.text(`Statut: ${facture.statut === 'payee' ? 'Payée' : 'Non payée'}`, 20, 70);
      
      // Infos du contrat
      if (selectedContrat.value) {
        doc.setFontSize(14);
        doc.text('Informations du contrat', 20, 90);
        
        doc.setFontSize(12);
        doc.text(`Référence: ${selectedContrat.value.reference}`, 20, 100);
        doc.text(`Locataire: ${selectedContrat.value.tenant?.name || 'N/A'}`, 20, 110);
        doc.text(`Bien: ${selectedContrat.value.property?.address || 'N/A'}`, 20, 120);
      }
      
      doc.save(`facture_${facture.numero.replace(/[/\s]/g, '_')}.pdf`);
    }
  } catch (error) {
    console.error('Erreur lors du téléchargement de la facture:', error);
    alert('Erreur lors du téléchargement de la facture');
  }
}

async function printFacture(facture) {
  if (!facture) return;
  
  try {
    if (facture.apiData?.invoice_file_path) {
      // Si la facture vient de l'API et a un fichier PDF
      const printWindow = window.open(`http://localhost:8000/storage/${facture.apiData.invoice_file_path}`, '_blank');
      printWindow.addEventListener('load', () => {
        printWindow.print();
      });
    } else if (facture.isGenerated) {
      // Pour les factures générées, créer un élément HTML à imprimer
      const printContent = `
        <div style="padding: 20px; font-family: Arial, sans-serif;">
          <h1 style="text-align: center;">Facture ${facture.numero}</h1>
          <div style="margin-top: 30px;">
            <p><strong>Date:</strong> ${facture.date}</p>
            <p><strong>Période:</strong> ${facture.periode}</p>
            <p><strong>Montant:</strong> ${facture.montant} MAD</p>
            <p><strong>Statut:</strong> ${facture.statut === 'payee' ? 'Payée' : 'Non payée'}</p>
          </div>
          <div style="margin-top: 30px; border-top: 1px solid #ccc; padding-top: 20px;">
            <h2>Informations du contrat</h2>
            <p><strong>Référence:</strong> ${selectedContrat.value?.reference || 'N/A'}</p>
            <p><strong>Locataire:</strong> ${selectedContrat.value?.tenant?.name || 'N/A'}</p>
            <p><strong>Bien:</strong> ${selectedContrat.value?.property?.address || 'N/A'}</p>
          </div>
        </div>
      `;
      
      const printWindow = window.open('', '_blank');
      printWindow.document.write('<html><head><title>Facture</title></head><body>');
      printWindow.document.write(printContent);
      printWindow.document.write('</body></html>');
      printWindow.document.close();
      
      printWindow.onload = function() {
        printWindow.print();
        printWindow.onafterprint = function() {
          printWindow.close();
        };
      };
    }
  } catch (error) {
    console.error('Erreur lors de l\'impression de la facture:', error);
    alert('Erreur lors de l\'impression de la facture');
  }
}

// Functions for contract download and print
async function downloadContract(contrat) {
  if (!contrat || !contrat.contract_file_path) {
    alert('Aucun fichier de contrat disponible pour le téléchargement');
    return;
  }
  
  try {
    // Open the contract PDF file in a new tab for download
    const link = document.createElement('a');
    link.href = `http://localhost:8000/storage/${contrat.contract_file_path}`;
    link.download = `contrat_${contrat.reference || 'document'}.pdf`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error('Erreur lors du téléchargement du contrat:', error);
    alert('Erreur lors du téléchargement du contrat');
  }
}

async function printContract(contrat) {
  if (!contrat || !contrat.contract_file_path) {
    alert('Aucun fichier de contrat disponible pour l\'impression');
    return;
  }
  
  try {
    // Open the contract PDF in a new window and trigger print
    const printWindow = window.open(`http://localhost:8000/storage/${contrat.contract_file_path}`, '_blank');
    
    // Wait for the PDF to load then trigger print
    printWindow.addEventListener('load', () => {
      setTimeout(() => {
        printWindow.print();
      }, 1000); // Give time for PDF to fully load
    });
  } catch (error) {
    console.error('Erreur lors de l\'impression du contrat:', error);
    alert('Erreur lors de l\'impression du contrat');
  }
}

// Computed pour la facture actuelle
const currentFacture = computed(() => {
  if (!factures.value || factures.value.length === 0) {
    return { statut: '', numero: '', date: '' }
  }
  return factures.value[currentFactureIndex.value] || factures.value[0]
})

// Add facture modal state
const showAddFactureModal = ref(false)
const isSubmittingFacture = ref(false)

// Add facture form
const factureForm = ref({
  contract_id: null,
  issue_date: '',
  due_date: '',
  period: '',
  amount_excl_tax: 0,
  tax_amount: 0,
  total_amount: 0,
  status: '',
  payment_date: '',
  payment_method: '',
  notes: '',
  invoice_file: null,
  payment_proof: null
})

// Functions for add facture modal
function openAddFactureModal() {
  if (!selectedContrat.value) return
  
  // Reset form
  factureForm.value = {
    contract_id: selectedContrat.value.id,
    issue_date: '',
    due_date: '',
    period: '',
    amount_excl_tax: 0,
    tax_amount: 0,
    total_amount: 0,
    status: '',
    payment_date: '',
    payment_method: '',
    notes: '',
    invoice_file: null,
    payment_proof: null
  }
  
  showAddFactureModal.value = true
}

function closeAddFactureModal() {
  showAddFactureModal.value = false
  factureForm.value = {
    contract_id: null,
    issue_date: '',
    due_date: '',
    period: '',
    amount_excl_tax: 0,
    tax_amount: 0,
    total_amount: 0,
    status: '',
    payment_date: '',
    payment_method: '',
    notes: '',
    invoice_file: null,
    payment_proof: null
  }
}

function calculateTotal() {
  const amountExclTax = parseFloat(factureForm.value.amount_excl_tax) || 0
  const taxAmount = parseFloat(factureForm.value.tax_amount) || 0
  factureForm.value.total_amount = amountExclTax + taxAmount
}

function handleInvoiceFileUpload(event) {
  const file = event.target.files[0]
  if (file) {
    factureForm.value.invoice_file = file
  }
}

function handlePaymentProofUpload(event) {
  const file = event.target.files[0]
  if (file) {
    factureForm.value.payment_proof = file
  }
}

async function submitFacture() {
  if (!factureForm.value.contract_id) return
  
  isSubmittingFacture.value = true
  try {
    // Create FormData object for file upload
    const formData = new FormData()
    
    // Add all form fields to FormData
    Object.keys(factureForm.value).forEach(key => {
      if (factureForm.value[key] !== null && factureForm.value[key] !== '') {
        formData.append(key, factureForm.value[key])
      }
    })
    
    // Import and use the factureService instead of raw fetch
    const factureService = await import('@/services/factureService').then(m => m.default)
    const result = await factureService.createFactureWithFiles(formData)
    
    if (result.success) {
      // Close modal
      closeAddFactureModal()
      
      // Refresh factures list
      await generateFactures(selectedContrat.value)
      
      // Show success message
      alert('Facture créée avec succès!')
    } else {
      console.error('Error creating invoice:', result)
      alert('Erreur lors de la création de la facture: ' + (result.message || 'Erreur inconnue'))
    }
  } catch (error) {
    console.error('Error submitting facture:', error)
    alert('Erreur lors de la création de la facture: ' + (error.response?.data?.message || error.message || 'Erreur inconnue'))
  } finally {
    isSubmittingFacture.value = false
  }
}
</script>

<script>
export default {
  name: 'ContratsView',
}
</script>

<style scoped>
.contrats-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 70px);
  margin-left: 270px;
  background: #f5f7fa;
}
.contrats-header {
  display: flex;
  align-items: center;
  margin: 24px 0 24px 0;
  padding-left: 24px;
}
.contrats-icon {
  width: 38px;
  height: 38px;
  margin-right: 12px;
}
.contrats-title {
  font-size: 2rem;
  font-weight: 700;
  color: #22305a;
  text-align: left;
}
.contrats-content {
  flex: 1;
  display: flex;
  flex-direction: column;
}
.contrats-filtres {
  display: flex;
  gap: 16px;
  margin-bottom: 18px;
  justify-content: center;
  flex-wrap: wrap;
  padding: 0 20px;
}
.contrats-filtre-input {
  padding: 8px 16px;
  border-radius: 6px;
  border: 1px solid #ccc;
  font-size: 1rem;
  width: 200px;
  transition: all 0.2s ease;
}
.contrats-filtre-input:focus {
  border-color: #22305a;
  box-shadow: 0 0 0 2px rgba(34, 48, 90, 0.1);
  outline: none;
}
.contrats-filtre-select {
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid #ccc;
  font-size: 1rem;
  min-width: 150px;
  cursor: pointer;
  transition: all 0.2s ease;
}
.contrats-filtre-select:focus {
  border-color: #22305a;
  box-shadow: 0 0 0 2px rgba(34, 48, 90, 0.1);
  outline: none;
}
.contrats-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  margin-bottom: 24px;
  overflow: hidden;
}
.contrats-table th, .contrats-table td {
  padding: 12px 10px;
  text-align: left;
  font-size: 1rem;
}
.contrats-table th {
  background: #bfc7d5;
  color: #22305a;
  font-weight: 700;
}
.contrats-table tr:not(:last-child) {
  border-bottom: 1px solid #f0f0f0;
}
.statut-actif {
  color: #3ba935;
  font-weight: 700;
}
.statut-ech {
  color: #e74c3c;
  font-weight: 700;
}
.action {
  cursor: pointer;
  margin-right: 6px;
  font-size: 1.2rem;
}

.action-icon {
  width: 18px;
  height: 18px;
  transition: transform 0.2s ease;
}

.action-icon:hover {
  transform: scale(1.1);
}

.delete-icon:hover {
  filter: sepia(1) saturate(5) hue-rotate(340deg);
}
.contrats-pagination {
  display: flex;
  justify-content: center;
  gap: 6px;
  margin-bottom: 12px;
}
.contrats-page-btn {
  background: #f5f7fa;
  border: 1px solid #bfc7d5;
  color: #22305a;
  border-radius: 4px;
  padding: 6px 14px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.contrats-page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.contrats-page-btn.active {
  background: #22305a;
  border-color: #22305a;
  color: white;
}

.contrats-page-btn:not(:disabled):hover {
  background: #bfc7d5;
  transform: translateY(-1px);
}
.contrats-btn-row {
  margin-top: auto;
  padding: 0 24px 24px 24px;
}
.contrats-btn {
  width: 100%;
  background: #3ba935;
  color: #fff;
  font-size: 1.6rem;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  padding: 18px 0;
  cursor: pointer;
  transition: background 0.2s;
}
.contrats-btn:hover {
  background: #2e8b2e;
}
@media (max-width: 900px) {
  .contrats-container {
    margin-left: 0;
  }
  .contrats-btn-row {
    padding: 0 8px 24px 8px;
  }
}
.modal-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

/* Modern Edit Modal Styles */
.edit-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(2px);
}

.edit-modal {
  background: #fff;
  border-radius: 16px;
  width: 90%;
  max-width: 900px;
  max-height: 90vh;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.edit-modal-header {
  background: linear-gradient(135deg, #22305a, #1a2847);
  color: white;
  padding: 24px 32px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-left h2 {
  margin: 0;
  font-size: 1.8rem;
  font-weight: 700;
}

.header-left p {
  margin: 4px 0 0 0;
  opacity: 0.9;
  font-size: 1rem;
}

.close-btn {
  background: none;
  border: none;
  color: white;
  font-size: 2rem;
  cursor: pointer;
  padding: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  transform: rotate(90deg);
}

.edit-modal-body {
  padding: 32px;
  overflow-y: auto;
  flex: 1;
  background: #f8f9fa;
}

.info-card {
  background: white;
  border-radius: 12px;
  margin-bottom: 24px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  border: 1px solid #e9ecef;
}

.info-card.readonly {
  border-left: 4px solid #6c757d;
}

.info-card.editable {
  border-left: 4px solid #28a745;
}

.card-header {
  padding: 20px 24px;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  align-items: center;
  gap: 16px;
}

.icon {
  font-size: 1.5rem;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.card-header h3 {
  margin: 0;
  font-size: 1.3rem;
  font-weight: 600;
  color: #2c3e50;
}

.card-header p {
  margin: 2px 0 0 0;
  font-size: 0.9rem;
  color: #6c757d;
}

.info-grid {
  padding: 24px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
}

.info-item label {
  display: block;
  font-size: 0.85rem;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
}

.readonly-value {
  background: #f8f9fa;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 1rem;
  color: #495057;
  border: 1px solid #e9ecef;
}

.form-grid {
  padding: 24px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
}

.form-field label {
  display: block;
  font-size: 0.9rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 8px;
}

.form-field input,
.form-field select,
.form-field textarea {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.2s ease;
  background: white;
  box-sizing: border-box;
}

.form-field input:focus,
.form-field select:focus,
.form-field textarea:focus {
  outline: none;
  border-color: #28a745;
  box-shadow: 0 0 0 3px rgba(40, 167, 69, 0.1);
}

.form-field textarea {
  resize: vertical;
  min-height: 80px;
  font-family: inherit;
}

.input-with-icon {
  position: relative;
}

.input-suffix {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.9rem;
  color: #6c757d;
  font-weight: 600;
  pointer-events: none;
}

.calculated-field {
  position: relative;
  display: flex;
  align-items: center;
}

.calculated-field input {
  background: #f8f9fa !important;
  color: #495057 !important;
  font-weight: 600 !important;
  border: 2px solid #e9ecef !important;
  cursor: not-allowed !important;
  padding-right: 45px !important;
}

.calc-icon {
  position: absolute;
  right: 16px;
  font-size: 1.1rem;
  color: #6c757d;
  pointer-events: none;
}

.edit-modal-footer {
  padding: 24px 32px;
  background: #fff;
  border-top: 1px solid #e9ecef;
  display: flex;
  justify-content: flex-end;
  gap: 16px;
}

.btn-cancel,
.btn-save {
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-cancel {
  background: #f8f9fa;
  color: #6c757d;
  border: 2px solid #e9ecef;
}

.btn-cancel:hover:not(:disabled) {
  background: #e9ecef;
  color: #495057;
}

.btn-save {
  background: linear-gradient(135deg, #28a745, #20c997);
  color: white;
  box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);
}

.btn-save:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(40, 167, 69, 0.4);
}

.btn-cancel:disabled,
.btn-save:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none !important;
}

.loading {
  display: flex;
  align-items: center;
  gap: 8px;
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.error-alert {
  background: #fff5f5;
  border: 1px solid #fed7d7;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 20px;
  color: #c53030;
  font-weight: 500;
}

.error-icon {
  font-size: 1.2rem;
}

@media (max-width: 768px) {
  .edit-modal {
    width: 95%;
    margin: 20px;
  }
  
  .edit-modal-header {
    padding: 16px 20px;
  }
  
  .edit-modal-body {
    padding: 20px;
  }
  
  .form-grid,
  .info-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  
  .edit-modal-footer {
    padding: 16px 20px;
    flex-direction: column;
  }
}

/* Document Upload Styles */
.document-section {
  padding: 20px;
}

.current-file {
  margin-bottom: 20px;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.file-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.file-icon {
  font-size: 1.5rem;
  color: #6c757d;
}

.file-details {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.file-name {
  font-weight: 600;
  color: #495057;
  font-size: 0.9rem;
}

.file-path {
  font-size: 0.8rem;
  color: #6c757d;
  margin-top: 2px;
}

.view-file-btn {
  background: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 6px 12px;
  font-size: 0.8rem;
  cursor: pointer;
  transition: background 0.2s;
}

.view-file-btn:hover {
  background: #0056b3;
}

.file-upload-area {
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #fafbfc;
}

.file-upload-area:hover {
  border-color: #28a745;
  background: #f8fff9;
}

.file-upload-area.has-file {
  border-color: #28a745;
  background: #f8fff9;
}

.upload-content {
  cursor: pointer;
}

.upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.upload-icon {
  font-size: 2rem;
  color: #6c757d;
}

.upload-placeholder span {
  font-weight: 600;
  color: #495057;
}

.upload-placeholder small {
  color: #6c757d;
  font-size: 0.8rem;
}

.upload-success {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #28a745;
  font-weight: 600;
}

.remove-file-btn {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1rem;
  pointer-events: all;
  transition: transform 0.2s;
}

.remove-file-btn:hover {
  transform: scale(1.2);
}
/* Enhanced Contract Modal Styles */
.contrat-modal-enhanced {
  background: #fff;
  border-radius: 20px;
  min-width: 1000px;
  max-width: 1200px;
  min-height: 800px;
  max-height: 90vh;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.contrat-modal {
  background: #fff;
  border-radius: 12px;
  min-width: 420px;
  max-width: 600px;
  box-shadow: 0 2px 16px rgba(0,0,0,0.18);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.contrat-modal-header {
  background: #22305a;
  color: #fff;
  font-size: 1.3rem;
  font-weight: 700;
  padding: 18px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.contrat-modal-close {
  font-size: 2.2rem;
  font-weight: 400;
  cursor: pointer;
  margin-left: 18px;
}
.contrat-modal-rep-block {
  background: #f5f7fa;
  padding: 18px 24px 10px 24px;
}
.contrat-modal-rep-title {
  color: #22305a;
  font-size: 1.2rem;
  font-weight: 700;
  margin-bottom: 6px;
}
.contrat-modal-rep-info {
  color: #222;
  font-size: 1.05rem;
  margin-bottom: 0;
}
.contrat-modal-content {
  padding: 24px;
  font-size: 1.1rem;
  color: #222;
}
.contrat-modal-content b {
  font-size: 1.15rem;
  display: block;
  margin-bottom: 10px;
}
.contrat-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  padding: 16px 24px 18px 24px;
  background: #f5f7fa;
}
.contrat-modal-btn {
  background: #f5f7fa;
  color: #22305a;
  border: 1px solid #bfc7d5;
  border-radius: 6px;
  padding: 8px 18px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}
.contrat-modal-btn:hover {
  background: #e6e6e6;
}

.modal-factures-placeholder, .modal-edit-placeholder {
  background: #f5f7fa;
  border: 1px dashed #bfc7d5;
  border-radius: 8px;
  min-width: 220px;
  min-height: 100px;
  margin-bottom: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #888;
  font-size: 1.1rem;
}
.form-group {
  margin-bottom: 14px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}
.form-group label {
  font-weight: 600;
  margin-bottom: 4px;
  color: #22305a;
}
.form-group input, .form-group select {
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid #bfc7d5;
  font-size: 1rem;
  width: 240px;
}
.create-contrat-modal {
  background: #fff;
  border-radius: 16px;
  min-width: 720px;
  max-width: 900px;
  box-shadow: 0 2px 16px rgba(0,0,0,0.18);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding-bottom: 18px;
}
.create-contrat-header {
  background: #22305a;
  color: #cbb87c;
  font-size: 2.1rem;
  font-weight: 700;
  padding: 24px 0;
  text-align: center;
  letter-spacing: 2px;
}
.create-contrat-form {
  padding: 18px 24px 0 24px;
  display: flex;
  flex-direction: column;
  gap: 18px;
}
.create-contrat-row {
  display: flex;
  gap: 18px;
}
.create-contrat-block {
  border: 1px solid #bfc7d5;
  border-radius: 10px;
  padding: 16px 18px 12px 18px;
  flex: 1;
  min-width: 260px;
  margin-bottom: 0;
}
.create-contrat-block legend {
  color: #22305a;
  font-size: 1.2rem;
  font-weight: 700;
  margin-bottom: 8px;
}
.form-group-row {
  display: flex;
  gap: 12px;
  margin-bottom: 10px;
  align-items: center;
}
.create-contrat-block input,
.create-contrat-block textarea {
  margin-bottom: 8px;
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid #bfc7d5;
  font-size: 1rem;
  width: 100%;
}
.create-contrat-block textarea {
  min-height: 48px;
  resize: vertical;
}
.dropdown-btn {
  background: #f5f7fa;
  border: 1px solid #bfc7d5;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 1.1rem;
  cursor: pointer;
}
.type-btn {
  background: #f5f7fa;
  border: 1px solid #bfc7d5;
  border-radius: 6px;
  padding: 6px 14px;
  font-size: 1rem;
  cursor: pointer;
  margin-right: 4px;
}
.type-btn-active {
  background: #3ba935;
  color: #fff;
  border: 1px solid #3ba935;
}
.echeance-btn {
  background: #f5f7fa;
  border: 1px solid #bfc7d5;
  border-radius: 6px;
  padding: 6px 14px;
  font-size: 1rem;
  cursor: pointer;
  margin-right: 4px;
}
.echeance-btn-active {
  background: #3ba935;
  color: #fff;
  border: 1px solid #3ba935;
}
.upload-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  color: #22305a;
  font-weight: 600;
  font-size: 1.1rem;
}
.upload-icon {
  font-size: 1.5rem;
  margin-right: 8px;
}
.upload-success {
  color: #3ba935;
  font-size: 0.9rem;
  margin-left: 10px;
}
.create-contrat-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 18px;
  padding: 0 4px;
}
.create-btn {
  background: #3ba935;
  color: #fff;
  font-size: 1.3rem;
  font-weight: 700;
  border: none;
  border-radius: 8px;
  padding: 10px 32px;
  cursor: pointer;
  transition: background 0.2s;
}
.create-btn:hover {
  background: #2e8b2e;
}
@media (max-width: 900px) {
  .create-contrat-modal {
    min-width: 90vw;
    max-width: 98vw;
    padding-bottom: 8px;
  }
  .create-contrat-form {
    padding: 8px 4px 0 4px;
  }
  .create-contrat-row {
    flex-direction: column;
    gap: 8px;
  }
}

/* Styles pour le modal slideshow des factures */
.factures-modal {
  background: #fff;
  border-radius: 12px;
  min-width: 800px;
  max-width: 1000px;
  min-height: 600px;
  box-shadow: 0 2px 16px rgba(0,0,0,0.18);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.factures-modal-header {
  background: #22305a;
  color: #fff;
  font-size: 1.3rem;
  font-weight: 700;
  padding: 18px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.factures-modal-close {
  font-size: 2.2rem;
  font-weight: 400;
  cursor: pointer;
}
.factures-modal-rep {
  background: #f5f7fa;
  padding: 18px 24px;
  color: #22305a;
  font-size: 1.1rem;
}
.factures-preview {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 32px 24px;
  flex: 1;
}
.nav-btn {
  background: #f5f7fa;
  border: 1px solid #bfc7d5;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.5rem;
  color: #22305a;
  transition: background 0.2s;
}
.nav-btn:hover {
  background: #e6e6e6;
}
.facture-preview-content {
  flex: 1;
  margin: 0 24px;
  min-height: 300px;
  background: #f9f9f9;
  border: 1px solid #ddd;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  color: #666;
}
.facture-status-btn {
  background: #3ba935;
  color: #fff;
  padding: 12px 32px;
  border-radius: 6px;
  font-weight: 700;
  font-size: 1.1rem;
}
.factures-actions {
  display: flex;
  gap: 16px;
  justify-content: center;
  padding: 24px;
  background: #f5f7fa;
}
.action-btn {
  background: none;
  border: none;
  color: #22305a;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 6px;
  transition: background 0.2s;
}
.action-btn:hover {
  background: #e6e6e6;
}
.factures-thumbnails {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px;
  background: #f5f7fa;
}
.thumbnails-container {
  display: flex;
  gap: 12px;
  overflow-x: auto;
  flex: 1;
}
.thumbnail {
  background: #fff;
  border: 2px solid #ddd;
  border-radius: 8px;
  padding: 12px;
  min-width: 120px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s;
  font-size: 0.9rem;
}
.thumbnail.active {
  border-color: #22305a;
  background: #f0f0f0;
}
.thumbnail-status {
  background: #3ba935;
  color: #fff;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 600;
  margin-top: 8px;
}
.no-pdf {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px; /* Adjust height as needed */
  background: #f9f9f9;
  border: 1px dashed #bfc7d5;
  border-radius: 8px;
  color: #666;
  font-size: 1.1rem;
}
.badge-paid {
  background: #3ba935;
  color: #fff;
  padding: 6px 18px;
  border-radius: 20px;
  font-weight: bold;
}
.badge-unpaid {
  background: #b80000;
  color: #fff;
  padding: 6px 18px;
  border-radius: 20px;
  font-weight: bold;
}
.facture-design { background: #fff; border-radius: 8px; padding: 24px; box-shadow: 0 2px 8px #0001; min-width: 500px; max-width: 650px; margin: 0 auto; }
.facture-header { display: flex; justify-content: space-between; font-size: 1rem; margin-bottom: 12px; }
.facture-info { font-size: 1.1rem; margin-bottom: 12px; display: flex; align-items: center; gap: 16px; }
.badge-paid { background: #3ba935; color: #fff; padding: 6px 18px; border-radius: 20px; font-weight: bold; margin-left: 16px; }
.badge-unpaid { background: #b80000; color: #fff; padding: 6px 18px; border-radius: 20px; font-weight: bold; margin-left: 16px; }
.facture-table { width: 100%; border-collapse: collapse; margin-bottom: 12px; }
.facture-table th, .facture-table td { border: 1px solid #e1e5e9; padding: 8px; text-align: center; }
.facture-totaux { background: #f5f7fa; padding: 12px; border-radius: 6px; margin-bottom: 12px; }
.facture-actions { display: flex; gap: 16px; justify-content: flex-end; }
.btn-danger { background: #b80000; color: #fff; border: none; border-radius: 6px; padding: 8px 18px; }
.btn-disabled { background: #aaa; color: #fff; border: none; border-radius: 6px; padding: 8px 18px; }
.slide-nav { background: #fff; border: 1px solid #d1d5db; border-radius: 50%; width: 40px; height: 40px; font-size: 1.5rem; cursor: pointer; margin: 0 12px; }
.slide-nav:disabled { opacity: 0.4; cursor: not-allowed; }
.icon { margin-right: 6px; }

.facture-modal-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.facture-modal-modern {
  background: #f8f9fa;
  border-radius: 16px;
  min-width: 700px;
  max-width: 900px;
  width: 700px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.12);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: 18px;
  position: relative;
  overflow: hidden;
}
.facture-modal-header {
  background: #22305a;
  color: #fff;
  font-size: 1.25rem;
  font-weight: 700;
  padding: 18px 32px;
  border-radius: 16px 16px 0 0;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.close-btn {
  font-size: 2rem;
  cursor: pointer;
}
.facture-modal-rep {
  background: #f3f5f8;
  padding: 18px 32px;
  font-size: 1rem;
  color: #22305a;
  border-bottom: 1px solid #e1e5e9;
  width: 100%;
}
.rep-title {
  color: #1a4ca0;
  font-weight: bold;
  font-size: 1.1rem;
}
.facture-modal-content {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 0 0 0;
  min-height: 350px;
  width: 100%;
}
.facture-card-modern {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 2px 12px #0001;
  min-width: 340px;
  min-height: 380px;
  max-width: 400px;
  margin: 0 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  position: relative;
}
.facture-badge-modern {
  width: 100%;
  text-align: center;
  font-size: 1.3rem;
  font-weight: bold;
  border-radius: 8px 8px 0 0;
  padding: 12px 0;
  margin-bottom: 10px;
}

.facture-details-modern {
  margin: 0 15px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
  width: calc(100% - 30px);
  box-sizing: border-box;
}

.facture-pdf-container {
  margin: 0 15px 15px;
  width: calc(100% - 30px);
  box-sizing: border-box;
  overflow: hidden;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.facture-info-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  padding-bottom: 6px;
  border-bottom: 1px dashed #e0e0e0;
}

.facture-info-row:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}

.facture-info-label {
  font-weight: 600;
  color: #1a2a4f;
}

.facture-info-value {
  color: #333;
}
.badge-paid-modern {
  background: #3ba935;
  color: #fff;
}
.badge-unpaid-modern {
  background: #b80000;
  color: #fff;
}
.facture-actions-modern {
  display: flex;
  justify-content: center;
  gap: 32px;
  margin: 18px 0 12px 0;
}
.facture-action-btn-modern {
  background: none;
  border: none;
  color: #bfa046;
  font-size: 1.1rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: color 0.2s;
}
.facture-action-btn-modern:hover {
  color: #22305a;
}
.icon-gold {
  color: #bfa046;
  font-size: 1.3rem;
}
.slide-arrow-gold {
  background: #fff;
  border: 2px solid #bfa046;
  color: #bfa046;
  border-radius: 50%;
  width: 48px;
  height: 48px;
  font-size: 2rem;
  cursor: pointer;
  margin: 0 12px;
  transition: background 0.2s, color 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}
.slide-arrow-gold:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.facture-thumbnails-modern {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 24px;
  gap: 8px;
  width: 100%;
  max-width: 860px;
  overflow-x: auto;
  padding-bottom: 8px;
  box-sizing: border-box;
  background: transparent;
  position: relative;
}
.thumbnails-list-modern {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  width: 100%;
  max-width: 820px;
  padding: 0 8px;
}
.thumbnail-modern {
  background: #fff;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  min-width: 110px;
  min-height: 70px;
  padding: 8px 6px;
  text-align: center;
  font-size: 0.95rem;
  cursor: pointer;
  box-shadow: 0 1px 4px #0001;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transition: border 0.2s;
}
.thumbnail-modern.active {
  border: 2px solid #bfa046;
  background: #f8f9fa;
}
.facture-badge-mini-modern {
  margin-top: 4px;
  font-size: 0.85rem;
  border-radius: 6px;
  padding: 2px 10px;
  font-weight: bold;
}
.thumb-arrow-gold {
  background: #fff;
  border: 2px solid #bfa046;
  color: #bfa046;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  font-size: 1.2rem;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}
.icon-gold-img {
  width: 22px;
  height: 22px;
  object-fit: contain;
  margin-right: 6px;
}
.action-icon {
  width: 20px;
  height: 20px;
  object-fit: contain;
  vertical-align: middle;
  cursor: pointer;
  margin: 0 4px;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #22305a;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-text {
  margin-top: 16px;
  color: #666;
  font-size: 1.1rem;
}

.error-message {
  background: #fee;
  color: #c00;
  padding: 12px;
  border-radius: 6px;
  margin: 12px 0;
  text-align: center;
}

.empty-message {
  text-align: center;
  padding: 32px;
  color: #666;
  font-style: italic;
}

.no-factures {
  text-align: center;
  padding: 10px;
  color: #666;
  font-style: italic;
  width: 100%;
}

.no-rep-info {
  color: #666;
  font-style: italic;
  padding: 10px 0;
}

/* Enhanced Contract Modal Header */
.contrat-modal-header-enhanced {
  background: linear-gradient(135deg, #22305a 0%, #2c3e6b 100%);
  color: #fff;
  padding: 24px 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  border-radius: 20px 20px 0 0;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.header-icon {
  font-size: 2rem;
  background: rgba(255, 255, 255, 0.1);
  padding: 12px;
  border-radius: 12px;
  backdrop-filter: blur(10px);
}

.header-text h2 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  letter-spacing: -0.02em;
}

.contract-ref {
  color: #ffd700;
  font-weight: 800;
}

.header-subtitle {
  margin: 4px 0 0 0;
  font-size: 0.9rem;
  opacity: 0.8;
  font-weight: 400;
}

.contrat-modal-close-enhanced {
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: #fff;
  font-size: 1.8rem;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  backdrop-filter: blur(10px);
}

.contrat-modal-close-enhanced:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: scale(1.05);
}

/* Enhanced Contract Modal Info Section */
.contrat-modal-info-enhanced {
  background: linear-gradient(135deg, #f8f9fc 0%, #e8eaf6 100%);
  padding: 20px 32px;
  border-bottom: 1px solid #e1e5e9;
}

.info-card-compact {
  background: #fff;
  border-radius: 12px;
  padding: 16px 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  border: 1px solid #e8eaf6;
}

.info-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.info-icon {
  font-size: 1.5rem;
  background: linear-gradient(135deg, #22305a, #2c3e6b);
  color: #fff;
  padding: 8px;
  border-radius: 8px;
}

.info-header h3 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #22305a;
}

.info-grid-compact {
  display: flex;
  flex-direction: column;
}

.info-details {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}

.info-row:last-child {
  border-bottom: none;
}

.label {
  font-weight: 600;
  color: #555;
  font-size: 0.9rem;
}

.value {
  color: #333;
  font-weight: 500;
}

.empty-state {
  color: #666;
  font-style: italic;
  text-align: center;
  padding: 20px;
}

/* Enhanced Contract Modal Content */
.contrat-modal-content-enhanced {
  flex: 1;
  padding: 24px 32px;
  background: #fafbfc;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0; /* Important for flex children */
}

.pdf-viewer-container {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 500px; /* Minimum reasonable height */
}

.pdf-iframe {
  border: none;
  border-radius: 12px;
  width: 100%;
  height: 100%;
  flex: 1;
  min-height: 500px;
}

.no-pdf-enhanced {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-height: 500px;
  background: linear-gradient(135deg, #f8f9fc 0%, #e8eaf6 100%);
}

.no-pdf-content {
  text-align: center;
  max-width: 400px;
  padding: 40px;
}

.no-pdf-icon {
  font-size: 4rem;
  margin-bottom: 24px;
  opacity: 0.5;
}

.no-pdf-content h3 {
  margin: 0 0 12px 0;
  font-size: 1.5rem;
  color: #22305a;
  font-weight: 600;
}

.no-pdf-content p {
  margin: 0 0 24px 0;
  color: #666;
  font-size: 1rem;
  line-height: 1.5;
}

.upload-suggestion {
  margin-top: 24px;
}

.upload-btn-suggestion {
  background: linear-gradient(135deg, #22305a, #2c3e6b);
  color: #fff;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
}

.upload-btn-suggestion:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(34, 48, 90, 0.3);
}

.upload-icon {
  font-size: 1.2rem;
}

/* Enhanced Contract Modal Footer */
.contrat-modal-footer-enhanced {
  background: linear-gradient(135deg, #f8f9fc 0%, #e8eaf6 100%);
  padding: 20px 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid #e1e5e9;
  border-radius: 0 0 20px 20px;
}

.footer-info {
  display: flex;
  align-items: center;
  gap: 16px;
}

.contract-status {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: #22305a;
}

.status-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
}

.status-active {
  background: #28a745;
  box-shadow: 0 0 0 2px rgba(40, 167, 69, 0.3);
}

.status-pending {
  background: #ffc107;
  box-shadow: 0 0 0 2px rgba(255, 193, 7, 0.3);
}

.footer-actions {
  display: flex;
  gap: 12px;
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  text-decoration: none;
}

.action-btn.primary {
  background: linear-gradient(135deg, #22305a, #2c3e6b);
  color: #fff;
  box-shadow: 0 2px 8px rgba(34, 48, 90, 0.2);
}

.action-btn.primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(34, 48, 90, 0.3);
}

.action-btn.secondary {
  background: #fff;
  color: #666;
  border: 2px solid #e1e5e9;
}

.action-btn.secondary:hover {
  background: #f8f9fc;
  border-color: #22305a;
  color: #22305a;
}

.action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none !important;
}

.btn-icon {
  font-size: 1.1rem;
}

/* Enhanced Factures Modal Styles */
.facture-modal-overlay-enhanced {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  backdrop-filter: blur(5px);
}

.facture-modal-enhanced {
  background: #fff;
  border-radius: 20px;
  min-width: 1100px;
  max-width: 1300px;
  min-height: 950px;
  max-height: 95vh;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: modalSlideIn 0.3s ease-out;
}

.facture-modal-header-enhanced {
  background: linear-gradient(135deg, #22305a 0%, #2c3e6b 100%);
  color: #fff;
  padding: 24px 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: 20px 20px 0 0;
}

.facture-modal-close-enhanced {
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: #fff;
  font-size: 1.8rem;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  backdrop-filter: blur(10px);
}

.facture-modal-close-enhanced:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: scale(1.05);
}

.facture-modal-info-enhanced {
  background: linear-gradient(135deg, #f8f9fc 0%, #e8eaf6 100%);
  padding: 20px 32px;
  border-bottom: 1px solid #e1e5e9;
}

/* Enhanced Factures Content */
.facture-modal-content-enhanced {
  flex: 1;
  padding: 24px 32px;
  background: #fafbfc;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

/* Compact Navigation Header */
.facture-navigation-header {
  background: #fff;
  border-radius: 12px;
  padding: 16px 24px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  border: 1px solid #e8eaf6;
}

.facture-nav-controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
}

.nav-btn-compact {
  background: linear-gradient(135deg, #22305a, #2c3e6b);
  border: none;
  color: #fff;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(34, 48, 90, 0.2);
}

.nav-btn-compact:hover:not(:disabled) {
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(34, 48, 90, 0.3);
}

.nav-btn-compact:disabled {
  opacity: 0.3;
  cursor: not-allowed;
  transform: none !important;
}

.nav-icon {
  font-size: 1.2rem;
  font-weight: bold;
}

.facture-info-compact {
  display: flex;
  align-items: center;
  gap: 24px;
  flex: 1;
  justify-content: center;
}

.facture-status-compact {
  padding: 8px 16px;
  border-radius: 20px;
  font-weight: 700;
  font-size: 0.9rem;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.facture-status-compact.status-paid {
  background: linear-gradient(135deg, #28a745, #20c997);
  color: #fff;
}

.facture-status-compact.status-unpaid {
  background: linear-gradient(135deg, #dc3545, #c82333);
  color: #fff;
}

.facture-status-compact.status-none {
  background: linear-gradient(135deg, #6c757d, #5a6268);
  color: #fff;
}

.facture-counter-compact {
  background: #f8f9fc;
  color: #22305a;
  padding: 6px 12px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.9rem;
  border: 1px solid #e8eaf6;
}

/* Fallback Details in No-PDF State */
.facture-details-fallback {
  margin-top: 24px;
  padding: 20px;
  background: #f8f9fc;
  border-radius: 12px;
  border: 1px solid #e8eaf6;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #e8eaf6;
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-row .detail-label {
  font-weight: 600;
  color: #555;
  font-size: 0.9rem;
}

.detail-row .detail-value {
  color: #333;
  font-weight: 500;
  font-size: 0.9rem;
}



/* Enhanced Thumbnails */
.facture-thumbnails-enhanced {
  padding: 20px 32px;
  background: linear-gradient(135deg, #f8f9fc 0%, #e8eaf6 100%);
  border-top: 1px solid #e1e5e9;
}

.thumbnails-container {
  display: flex;
  gap: 12px;
  overflow-x: auto;
  padding: 8px 0;
}

.thumbnail-enhanced {
  background: #fff;
  border: 2px solid #e8eaf6;
  border-radius: 12px;
  padding: 12px 16px;
  min-width: 140px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.thumbnail-enhanced:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
}

.thumbnail-enhanced.active {
  border-color: #22305a;
  background: linear-gradient(135deg, #f8f9fc, #e8eaf6);
  transform: translateY(-2px);
}

.thumbnail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.thumbnail-ref {
  font-weight: 600;
  color: #22305a;
  font-size: 0.9rem;
}

.thumbnail-status {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.thumbnail-status.paid {
  background: #28a745;
}

.thumbnail-status.unpaid {
  background: #dc3545;
}

.thumbnail-date {
  font-size: 0.8rem;
  color: #666;
  text-align: center;
}

.facture-modal-footer-enhanced {
  background: linear-gradient(135deg, #f8f9fc 0%, #e8eaf6 100%);
  padding: 20px 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid #e1e5e9;
  border-radius: 0 0 20px 20px;
}

.facture-summary {
  font-weight: 600;
  color: #22305a;
  font-size: 1rem;
}

/* Responsive Design for Enhanced Modals */
@media (max-width: 1200px) {
  .contrat-modal-enhanced,
  .facture-modal-enhanced {
    min-width: 90vw;
    max-width: 95vw;
    margin: 10px;
  }
  
  .facture-navigation-header {
    padding: 12px 16px;
    margin-bottom: 16px;
  }
  
  .facture-nav-controls {
    gap: 12px;
  }
  
  .nav-btn-compact {
    width: 36px;
    height: 36px;
  }
  
  .pdf-viewer-container {
    min-height: 600px;
  }
}

@media (max-width: 768px) {
  .contrat-modal-enhanced,
  .facture-modal-enhanced {
    min-width: 95vw;
    max-width: 98vw;
    min-height: 90vh;
    border-radius: 16px;
  }
  
  .contrat-modal-header-enhanced,
  .facture-modal-header-enhanced {
    padding: 16px 20px;
    border-radius: 16px 16px 0 0;
  }
  
  .header-text h2 {
    font-size: 1.2rem;
  }
  
  .header-icon {
    font-size: 1.5rem;
    padding: 8px;
  }
  
  .contrat-modal-content-enhanced,
  .facture-modal-content-enhanced {
    padding: 16px 20px;
  }
  
  .pdf-viewer-container {
    min-height: 400px;
    flex: 1;
  }
  
  .facture-navigation-header {
    padding: 10px 12px;
    margin-bottom: 12px;
  }
  
  .facture-nav-controls {
    gap: 8px;
  }
  
  .nav-btn-compact {
    width: 32px;
    height: 32px;
  }
  
  .nav-icon {
    font-size: 1rem;
  }
  
  .facture-info-compact {
    gap: 12px;
  }
  
  .facture-status-compact {
    font-size: 0.8rem;
    padding: 6px 12px;
  }
  
  .facture-counter-compact {
    font-size: 0.8rem;
    padding: 4px 8px;
  }
  
  .pdf-iframe {
    min-height: 400px;
  }
  
  .no-pdf-enhanced {
    min-height: 400px;
  }
  
  .info-details {
    grid-template-columns: 1fr;
  }
  
  .footer-actions {
    flex-direction: column;
    gap: 8px;
    width: 100%;
  }
  
  .action-btn {
    width: 100%;
    justify-content: center;
  }
  
  .thumbnails-container {
    gap: 8px;
  }
  
  .thumbnail-enhanced {
    min-width: 120px;
    padding: 8px 12px;
  }
}

/* Add Facture Modal Styles */
.add-facture-modal {
  background: #fff;
  border-radius: 16px;
  min-width: 600px;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
  animation: modalSlideIn 0.3s ease-out;
}

.modal-header {
  background: linear-gradient(135deg, #22305a 0%, #2c3e6b 100%);
  color: #fff;
  padding: 24px 32px;
  border-radius: 16px 16px 0 0;
  position: relative;
}

.modal-header h3 {
  margin: 0 0 4px 0;
  font-size: 1.5rem;
  font-weight: 600;
}

.modal-subtitle {
  margin: 0;
  opacity: 0.8;
  font-size: 0.9rem;
}

.close-btn {
  position: absolute;
  right: 24px;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: #fff;
  font-size: 1.5rem;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: translateY(-50%) scale(1.1);
}

.facture-form {
  padding: 32px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
  color: #374151;
}

.form-group input,
.form-group select,
.form-group textarea {
  width: 100%;
  padding: 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.2s ease;
  box-sizing: border-box;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #22305a;
  box-shadow: 0 0 0 3px rgba(34, 48, 90, 0.1);
}

.form-group input[readonly] {
  background-color: #f9fafb;
  color: #6b7280;
}

.form-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
}

.btn-primary, .btn-secondary {
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
}

.btn-primary {
  background: linear-gradient(135deg, #22305a 0%, #2c3e6b 100%);
  color: #fff;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(34, 48, 90, 0.3);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.btn-secondary {
  background: #f3f4f6;
  color: #374151;
  border: 1px solid #d1d5db;
}

.btn-secondary:hover {
  background: #e5e7eb;
}

.action-btn.success {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  color: #fff;
}

.action-btn.success:hover {
  background: linear-gradient(135deg, #047857 0%, #065f46 100%);
  transform: translateY(-1px);
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}
</style> 